/**
 * User Management Hooks
 *
 * Provides hooks for user CRUD operations using createResourceHooks.
 * Includes additional custom hooks for invites, locales, etc.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createResourceHooks } from './createResourceHooks'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import { api } from '@/lib/api'
import type {
  User,
  UserListFilters,
  UserCreateInput,
  UserUpdateInput,
  UserInviteInput,
} from '@/types/user'

// ============================================================================
// Query Keys
// ============================================================================

export const userManagementKeys = createQueryKeys('users')

// ============================================================================
// Base CRUD Hooks (via createResourceHooks)
// ============================================================================

const userHooks = createResourceHooks<User>('users', userManagementKeys)

// Re-export with more descriptive names
export const useUserList = (
  params?: RAListParams & { filters?: UserListFilters },
  options?: { enabled?: boolean; staleTime?: number }
) => {
  // Build filter from UserListFilters
  const filter: Record<string, unknown> = { ...params?.filter }
  if (params?.filters?.customer_id) {
    filter.customer_id = params.filters.customer_id
  }
  if (params?.filters?.partner_id) {
    filter.partner_id = params.filters.partner_id
  }
  if (params?.filters?.q) {
    filter.q = params.filters.q
  }

  return userHooks.useList(
    {
      page: params?.page,
      perPage: params?.perPage,
      sort: params?.sort ?? { field: 'id', order: 'ASC' },
      filter,
    },
    options
  )
}

export const useUser = userHooks.useOne

export const useCreateUser = () => {
  const mutation = userHooks.useCreate()
  return {
    ...mutation,
    mutateAsync: (data: UserCreateInput) =>
      mutation.mutateAsync(data as Partial<User>),
    mutate: (data: UserCreateInput) => mutation.mutate(data as Partial<User>),
  }
}

export const useUpdateUser = () => {
  const mutation = userHooks.useUpdate()
  return {
    ...mutation,
    mutateAsync: (data: UserUpdateInput) =>
      mutation.mutateAsync({ id: data.id, data }),
    mutate: (data: UserUpdateInput) => mutation.mutate({ id: data.id, data }),
  }
}

export const useDeleteUser = userHooks.useDelete

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to fetch available locales
 */
export function useLocales(enabled = true) {
  return useQuery({
    queryKey: [...userManagementKeys.all, 'locales'],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get('/users/locales')
      return response.data
    },
    enabled,
    staleTime: 60 * 60 * 1000, // Cache for 1 hour
  })
}

/**
 * Get readable locale name
 */
export function getReadableLocaleName(locale: string): string {
  const localeNames: Record<string, string> = {
    en: 'English',
    'en-US': 'English (US)',
    'en-GB': 'English (UK)',
    'en-IE': 'English (IE)',
    nl: 'Nederlands',
    'nl-NL': 'Nederlands',
    de: 'Deutsch',
    'de-DE': 'Deutsch',
    fr: 'Français',
    'fr-FR': 'Français',
    es: 'Español',
    'es-ES': 'Español',
    it: 'Italiano',
    'it-IT': 'Italiano',
    pt: 'Português',
    'pt-PT': 'Português',
  }
  return localeNames[locale] || locale
}

/**
 * Hook to invite a user (send activation link)
 */
export function useInviteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UserInviteInput): Promise<void> => {
      await api.post('/send-activation-link', { user: data })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.all })
    },
  })
}

/**
 * Hook to resend activation link to an existing user
 */
export function useResendInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: number): Promise<void> => {
      await api.post('/send-activation-link', { user_id: userId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.all })
    },
  })
}
